import { AxiosResponse } from 'axios';
import axiosInstance from './axios';

/**
 * Carbon indices CMS.
 *
 * The platform wraps every response as { statusCode, message, data }, so these helpers
 * unwrap to the payload and callers never see the envelope. Existing screens do
 * `data?.data?.data` at the call site instead; doing it once here means a component
 * cannot forget.
 *
 * Validation failures come back as 400 with a structured body:
 *   { message, error_type: 'indices_validation_failed', errors: string[], warnings: string[] }
 * and concurrency failures as 409 with error_type 'indices_draft_conflict' or
 * 'indices_publish_conflict'. Those fields are preserved on the thrown error so the
 * screen can list the specific problems rather than a generic failure toast.
 */
export class IndicesApiError extends Error {
  errorType?: string;
  errors: string[];
  warnings: string[];
  status?: number;

  constructor(message: string, opts: {
    errorType?: string; errors?: string[]; warnings?: string[]; status?: number;
  } = {}) {
    super(message);
    this.name = 'IndicesApiError';
    this.errorType = opts.errorType;
    this.errors = opts.errors ?? [];
    this.warnings = opts.warnings ?? [];
    this.status = opts.status;
  }
}

function toIndicesError(error: any, fallback: string): IndicesApiError {
  const body = error?.response?.data;
  // A validation failure arrives as a list in `message`, which is what Nest's own
  // ValidationPipe produces and what the platform's ValidationExceptionFilter forwards.
  // A single-cause failure arrives as a string there.
  const listed = Array.isArray(body?.message) ? (body.message as string[]) : null;
  return new IndicesApiError(listed ? fallback : body?.message || error?.message || fallback, {
    errorType: body?.error_type,
    errors: listed ?? (Array.isArray(body?.errors) ? body.errors : []),
    warnings: Array.isArray(body?.warnings) ? body.warnings : [],
    status: error?.response?.status,
  });
}

const unwrap = (response: AxiosResponse) => response.data?.data ?? response.data;

export const getIndicesDraft = async (): Promise<any> => {
  try {
    return unwrap(await axiosInstance.get('/carbon-indices/draft'));
  } catch (error) {
    throw toIndicesError(error, 'Could not load the carbon indices draft');
  }
};

export const validateIndicesDraft = async (source: any): Promise<any> => {
  try {
    return unwrap(await axiosInstance.post('/carbon-indices/draft/validate', { source }));
  } catch (error) {
    throw toIndicesError(error, 'Could not validate the draft');
  }
};

export const saveIndicesDraft = async (payload: {
  source: any;
  note?: string;
  expected_updated_at?: string;
}): Promise<any> => {
  try {
    return unwrap(await axiosInstance.put('/carbon-indices/draft', payload));
  } catch (error) {
    throw toIndicesError(error, 'Could not save the draft');
  }
};

export const publishIndices = async (payload: {
  expected_updated_at: string;
  note?: string;
}): Promise<any> => {
  try {
    return unwrap(await axiosInstance.post('/carbon-indices/publish', payload));
  } catch (error) {
    throw toIndicesError(error, 'Could not publish');
  }
};

export const getIndicesRevisions = async (): Promise<any> => {
  try {
    return unwrap(await axiosInstance.get('/carbon-indices/revisions'));
  } catch (error) {
    throw toIndicesError(error, 'Could not load the revision history');
  }
};

export const restoreIndicesRevision = async (
  id: string,
  expectedUpdatedAt?: string | null,
  note?: string,
): Promise<any> => {
  try {
    return unwrap(
      await axiosInstance.post(`/carbon-indices/revisions/${id}/restore`, {
        note,
        // A restore overwrites the draft, so it carries the same lost-update token a save
        // does. Without it the server cannot tell that this page has gone stale.
        expected_updated_at: expectedUpdatedAt ?? undefined,
      }),
    );
  } catch (error) {
    throw toIndicesError(error, 'Could not restore that revision');
  }
};

export const retryIndicesDeploy = async (): Promise<any> => {
  try {
    return unwrap(await axiosInstance.post('/carbon-indices/deploy/retry', {}));
  } catch (error) {
    throw toIndicesError(error, 'Could not ask the website to rebuild');
  }
};

/**
 * Runs the platform's index computation, then the pre-fill reads what it wrote.
 *
 * Owned by the vcmqualityindices module, not by this screen. It recomputes all four
 * series in sequence and writes the team's tracking sheets. The API reference puts a full
 * run under a minute, with the quarterly indices the slowest at 15-20s each, so five
 * minutes is generous without leaving the button spinning on a run that has actually
 * died. The default axios timeout would abort a healthy run.
 *
 * A failure is not atomic: the run stops at the first error and does NOT roll back the
 * indices it already recomputed. That is why the pre-fill reports each series' own
 * computation time rather than one figure for all four.
 */
/**
 * Starts the recompute and returns as soon as it has started.
 *
 * This used to POST to /vcmqualityindices/index/all, which does the whole two minutes of
 * work before replying. The gateway closes any request at sixty seconds, so the reply
 * never arrived: the browser reported a bare network error for a run that was busy
 * succeeding. The platform now has an endpoint that starts the work and says so, and the
 * result is read from indices_computed_at on the draft.
 */
export const startRecompute = async (): Promise<any> => {
  try {
    return unwrap(await axiosInstance.post('/carbon-indices/recompute', {}));
  } catch (error) {
    throw toIndicesError(error, 'Could not start the recompute');
  }
};

/**
 * Uploads the pricing workbook and gets back a PROPOSAL. Writes nothing on the server.
 *
 * The multipart content type has to be set explicitly. axiosInstance defaults every
 * request to application/json, and that default wins over whatever the browser would have
 * chosen, so a FormData body goes out labelled as JSON and arrives with no file at all --
 * which shows up as "pricing.monthly.periods needs at least 2 periods, got 0" rather than
 * as an upload error. Every other upload in this codebase sets the header for the same
 * reason.
 */
export const extractIndices = async (files: { cbm?: File }): Promise<any> => {
  const form = new FormData();
  if (files.cbm) form.append('cbm', files.cbm);
  try {
    return unwrap(
      await axiosInstance.post('/carbon-indices/extract', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  } catch (error) {
    throw toIndicesError(error, 'Could not read the workbooks');
  }
};
